import {Collaborator, isDataPolicyGroupCollaborator, OwnerInfo} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ReviewLifecycleType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {REVIEW_LIFECYCLE_TYPE_TO_DISPLAY_TEXT} from './ReviewLifecyclesListing';

// TODO (BNCH-93539): remove when we sunset ENABLE_ENTRY_REVIEW_PROCESSES
export function areReviewLifecyclesEnabledForUser(): boolean {
  return BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES;
}

export function isAuthorReviewInReviewLifecyclesEnabled(): boolean {
  return BenchlingConfig.ENABLE_AUTHOR_REVIEW_IN_ENTRY_REVIEW_PROCESSES;
}

export function areTeamReviewsEnabled(): boolean {
  return BenchlingConfig.ENABLE_TEAM_REVIEW_IN_ENTRY_REVIEW_PROCESSES;
}

export function toPastTense(action: string): string {
  if (action.slice(-1) === 'e') {
    return `${action}d`;
  }
  return `${action}ed`;
}

export function getReviewLifecycleTypeText(lifecycleType?: ReviewLifecycleType): string | null {
  if (lifecycleType == null) {
    return null;
  }

  return REVIEW_LIFECYCLE_TYPE_TO_DISPLAY_TEXT[lifecycleType];
}

/**
 * Converts a list of names to a readable string with bolded elements and proper formatting e.g.:
 * for ['a', 'b', 'c'] it will output "a, b, and c".
 * for ['a', 'b'] it will output "a and b".
 * for ['a'] it will output "a".
 */
export function formatListWithBoldedNames(names: Array<string>): string {
  names = names.map((name) => `<b>${name}</b>`);
  if (names.length < 3) {
    return names.join(' and ');
  } else {
    names[names.length - 1] = `and ${names[names.length - 1]}`;
    return names.join(', ');
  }
}

/**
 * Given requiredReviewStageTeams, returns Review Lifecycle teams that are either missing or
 * whose members have NONE permissions for this project.
 *
 * A team is considered missing if neither the team nor its owning organization are granted full member permissions as collaborators.
 * If the organization is the owner and has full member permissions, then the team is not considered missing.
 */
export function getRlcTeamsWithoutMemberAccessToProject(
  requiredReviewStageTeams: Array<{id: string; name: string}>,
  collaborators: Array<Collaborator>,
  reviewLifecycleOrgId: string,
  projectOwnerId: string,
  projectOwnerInfo?: OwnerInfo
): Array<{id: string; name: string}> {
  const orgCollaborator = collaborators.find((collaborator) => collaborator.id === reviewLifecycleOrgId);
  if (
    (orgCollaborator != null &&
      isDataPolicyGroupCollaborator(orgCollaborator) &&
      orgCollaborator?.memberDataPolicyId != null) ||
    (projectOwnerId === reviewLifecycleOrgId && projectOwnerInfo?.memberDataPolicyId != null)
  ) {
    return [];
  }

  const requiredReviewStageTeamIds = new Set(requiredReviewStageTeams.map(({id}) => id));
  const collaboratorsWithoutMemberAccess = new Set(
    collaborators
      .filter(({id}) => requiredReviewStageTeamIds.has(id))
      .filter(
        (collaborator) =>
          isDataPolicyGroupCollaborator(collaborator) && collaborator.memberDataPolicyId == null
      )
      .map(({id}) => id)
  );

  const collaboratorIds = new Set(collaborators.map(({id}) => id));
  return requiredReviewStageTeams.filter(
    ({id: teamId}) => !collaboratorIds.has(teamId) || collaboratorsWithoutMemberAccess.has(teamId)
  );
}

/**
 * Returns if a team contains users that can act as a reviewer for a reviewable.
 * This happens if there are team users not included in the list of authors.
 * @param teamUserIds list of team user ids.
 * @param authorIds list of author ids.
 */
export function teamHasValidReviewersForReviewable(
  teamUserIds: Array<string>,
  authorIds: Array<string>
): boolean {
  return teamUserIds.filter((userId) => !authorIds.includes(userId)).length > 0;
}
